import React, { SyntheticEvent, useCallback, useLayoutEffect } from 'react';
import {
  useNavigate,
  useActionData,
  useAsyncValue,
  useSubmit,
  useParams,
} from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

// Components
import ContentCardResume from '../../../../../components/display/ContentCardResume';
import { ContentContainer } from '../../components/MainStyledComponents';

// Types
import { type WorkOrder } from '../../../../../types/workOrder';
import { createFormDataFromValues } from '../../../../../utils/forms';
import { getClassificationName } from '../../../../../utils/orders';
import { variant } from '../../../../../components/display/SpecialTag/types';

/**
 * Resumen de la OT creada dentro de un tram de recorrido de vigilancia.
 * Reutiliza ContentCardResume (mismo que WO y Campaign summary).
 */
export default function SurveillanceRouteOrderSummary(): JSX.Element {
  const { t } = useTranslation('pages');
  const { routeId } = useParams<{ routeId: string }>();
  const loaderData = useAsyncValue() as WorkOrder;
  const actionData = useActionData() as WorkOrder;
  const navigate = useNavigate();
  const submit = useSubmit();

  const workOrderData = actionData ?? loaderData;

  useLayoutEffect(() => {
    if (!workOrderData) navigate(`/surveillance-route/${routeId}/new-order`);
  }, [workOrderData]);

  const handleEdit = useCallback(() => {
    navigate(`/surveillance-route/${routeId}/new-order`);
  }, [routeId]);

  const handleSave = async (e: SyntheticEvent) => {
    e.preventDefault();
    const formData = createFormDataFromValues({
      intent: 'save-data',
    });
    submit(formData, {
      method: 'POST',
      action: '.',
    });
  };

  return (
    <ContentContainer>
      {workOrderData && (
        <ContentCardResume
          title={t('workOrders.summary')}
          roid={''}
          operator={workOrderData.operator}
          data={format(
            new Date(workOrderData.registerInitDate),
            'dd / MM / yyyy'
          )}
          hour={format(new Date(workOrderData.registerInitDate), 'HH:mm')}
          capitol={workOrderData.capitol}
          subCapitol={workOrderData.subCapitol}
          classification={getClassificationName(
            workOrderData.classification,
            workOrderData.classifications
          )}
          team={workOrderData.team}
          concession={workOrderData.concession}
          road={workOrderData.road}
          pkInit={`${workOrderData.pkInit} km`}
          pkEnd={`${workOrderData.pkEnd} km`}
          track={workOrderData.track}
          roadDirections={workOrderData.roadDirections}
          direction={workOrderData.direction}
          city={workOrderData.city}
          lane={workOrderData.lane}
          margin={workOrderData.margin}
          // Fix i18n 2026-07-03: pages:workOrders.editOT/saveAndReturn no
          // existen (se veían las keys crudas) → keys existentes de common:
          leftButton={t('common:buttons.edit')}
          rightButton={t('common:buttons.save')}
          description={workOrderData.description}
          notes={workOrderData.notes}
          onEditCommunication={handleEdit}
          onSaveCommunication={handleSave}
          tag={variant.SurveillanceRoute}
          tagContent={t('surveillance.title')}
        />
      )}
    </ContentContainer>
  );
}
